import { Clock } from "lucide-react";
import type { FengniaoDashboard } from "../hooks/useFengniaoDashboard";
import { formatBnb, formatDuration, formatToken } from "../lib/format";
import { StateBanner } from "./StateBanner";

interface LpTimePanelProps {
  dashboard: FengniaoDashboard;
}

function truncate(s: string, max = 15): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

export function LpTimePanel({ dashboard }: LpTimePanelProps) {
  const { lp, lpTime, stage } = dashboard;
  const stats = lpTime.stats;

  const rows: { label: string; value: string; accent?: string }[] = [
    { label: "钱包 LP", value: formatToken(lp.walletBalance, lp.symbol) },
    { label: "最低 LP", value: formatToken(lpTime.minLpHolding, lp.symbol) },
    { label: "已登记 LP", value: formatToken(lpTime.registeredBalance, lp.symbol) },
    { label: "持有时长", value: formatDuration(lpTime.holdingSeconds) },
    {
      label: "可领 BNB",
      value: formatBnb(lpTime.claimableBnb),
      accent: lpTime.claimableBnb > 0n ? "text-bnb" : undefined,
    },
    { label: "池子总分", value: truncate((stats?.[0] ?? 0n).toString()) },
    { label: "累计已发", value: formatBnb(stats?.[2] ?? 0n) },
    { label: "池中余额", value: formatBnb(stats?.[3] ?? 0n) },
    { label: "参与人数", value: (stats?.[1] ?? 0n).toString() },
  ];

  return (
    <section className="glass p-5 lg:p-6">
      <header className="flex items-center gap-2.5 mb-2">
        <Clock className="w-5 h-5 text-bnb" strokeWidth={1.75} aria-hidden />
        <h3 className="text-sm font-mono uppercase tracking-wider text-ink">
          LP 时间加权分红 BNB
        </h3>
      </header>
      <p className="text-xs text-ink-dim leading-relaxed mb-4">
        你的 LP 越多、拿得越久，每次税收分到的 BNB 就越多。LP 一直在你钱包里，不用质押。
      </p>

      {lpTime.decreased ? (
        <div className="mb-4">
          <StateBanner tone="warning">你的 LP 比之前少了，奖励仓位会被重置</StateBanner>
        </div>
      ) : null}
      {stage.lpTimePaused ? (
        <div className="mb-4">
          <StateBanner tone="warning">BNB 分红已暂停</StateBanner>
        </div>
      ) : null}

      <dl className="grid gap-x-6 gap-y-3 lg:grid-cols-2">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline justify-between gap-3 border-b border-white/[0.04] pb-2"
          >
            <dt className="text-xs text-ink-dim shrink-0">{row.label}</dt>
            <dd
              className={`text-sm font-mono tabular-nums text-right break-all ${row.accent ?? "text-ink"}`}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
